import React from "react";
import { t } from "i18next";
import { FloatingLabel, Form, InputGroup, Modal } from "react-bootstrap";

import { StandardProjectModalProps } from "./StandardProjectModal.types.tsx";

import { ActionButton } from "../../components/Buttons.tsx";
import { StyledCreatableSelect } from "../../components/Inputs.tsx";

const StandardProjectModal: React.FC<StandardProjectModalProps> = ({
  capacities,
  efficiency,
  formData,
  handleChange,
  handleClose,
  handleSelectGeneric,
  handleSubmit,
  heatExchanger,
  locations,
  refrigerant,
  show,
  systems,
  technology,
  validated,
  voltages,
  title,
  buttonLabel,
  unitSystem,
}) => {
  const disableNumberScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  // Al editar, system_id llega como numero desde la hoja guardada; al crear, como texto del select.
  const isCondensingUnit = ["5", "6"].includes(String(formData?.system_id));

  // Cada select se habilita por el campo anterior, no por tener opciones: las listas no se vacian
  // al cambiar de sistema ni al abrir un panel nuevo, y conservarian las del calculo anterior.
  // La eficiencia sigue al intercambiador, salvo en la condensadora, que no lleva y va tras la capacidad.
  const efficiencyUnlocked = isCondensingUnit
    ? !!formData.nominal_capacity
    : !!formData.heat_exchanger_id;

  const locationOptions = locations?.map((loc) => ({
    value: loc.name,
    label: loc.name,
  }));

  return (
    <Modal show={true} onHide={handleClose} centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {/* Project Name */}
          <FloatingLabel
            controlId="floatingProjectName"
            label={t("standardProjectModal.projectName")}
            className="mb-3"
          >
            <Form.Control
              type="text"
              name="name"
              placeholder=""
              value={formData.name}
              onChange={handleChange}
              required
            />
            <Form.Control.Feedback type="invalid">
              {t("standardProjectModal.errors")}
            </Form.Control.Feedback>
          </FloatingLabel>

          {/* Description */}
          <FloatingLabel
            controlId="floatingDescription"
            label={t("standardProjectModal.description")}
            className="mb-3"
          >
            <Form.Control
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder=""
            />
            <Form.Control.Feedback type="invalid">
              {t("standardProjectModal.errors")}
            </Form.Control.Feedback>
          </FloatingLabel>

          {/* System */}
          <FloatingLabel
            controlId="floatingSystem"
            label={t("standardProjectModal.system")}
            className="mb-3"
          >
            <Form.Select
              name="system_id"
              value={formData.system_id}
              onChange={handleSelectGeneric}
              required
            >
              <option value="" disabled>
                {t("standardProjectModal.selectOption")}
              </option>
              {systems?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>

          {/* Voltage */}
          <FloatingLabel
            controlId="floatingVoltages"
            label={t("standardProjectModal.voltages")}
            className="mb-3"
          >
            <Form.Select
              name="voltage_id"
              value={formData.voltage_id}
              onChange={handleSelectGeneric}
              disabled={!formData.system_id || voltages?.length === 0}
              required
            >
              <option value="" disabled>
                {t("standardProjectModal.selectOption")}
              </option>
              {voltages?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>

          {/* Technology */}
          <FloatingLabel
            controlId="floatingTechnology"
            label={t("standardProjectModal.technology")}
            className="mb-3"
          >
            <Form.Select
              name="technology_id"
              value={formData.technology_id}
              onChange={handleSelectGeneric}
              disabled={!formData.voltage_id || technology?.length === 0}
              required
            >
              <option value="" disabled>
                {t("standardProjectModal.selectOption")}
              </option>
              {technology?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>

          {/* Refrigerant */}
          <FloatingLabel
            controlId="floatingRefrigerant"
            label={t("standardProjectModal.refrigerant")}
            className="mb-3"
          >
            <Form.Select
              name="refrigerant_id"
              value={formData.refrigerant_id}
              onChange={handleSelectGeneric}
              disabled={!formData.technology_id || refrigerant?.length === 0}
              required
            >
              <option value="" disabled>
                {t("standardProjectModal.selectOption")}
              </option>
              {refrigerant?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>

          {/* Nominal Capacity */}
          <InputGroup className="mb-3">
            <FloatingLabel
              controlId="floatingCapacities"
              label={t("standardProjectModal.capacities")}
            >
              <Form.Select
                name="nominal_capacity"
                value={formData.nominal_capacity}
                onChange={handleSelectGeneric}
                disabled={!formData.refrigerant_id || capacities?.length === 0}
                required
              >
                <option value="" disabled>
                  {t("standardProjectModal.selectOption")}
                </option>
                {capacities?.map((capacity, i) => (
                  <option key={i} value={capacity}>
                    {capacity}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            <InputGroup.Text>{t(`units.${unitSystem}.weight`)}</InputGroup.Text>
          </InputGroup>

          {/* Heat Exchanger: las unidades condensadoras no llevan, asi que ni se muestra */}
          {!isCondensingUnit && (
            <FloatingLabel
              controlId="floatingHeatExchanger"
              label={t("standardProjectModal.heatExchanger")}
              className="mb-3"
            >
              <Form.Select
                name="heat_exchanger_id"
                value={formData.heat_exchanger_id}
                onChange={handleSelectGeneric}
                disabled={!formData.nominal_capacity || heatExchanger?.length === 0}
                required
              >
                <option value="" disabled>
                  {t("standardProjectModal.selectOption")}
                </option>
                {heatExchanger?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.type || s.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          )}

          {/* Efficiency */}
          <FloatingLabel
            controlId="floatingEfficiency"
            label={t("standardProjectModal.efficiency")}
            className="mb-3"
          >
            <Form.Select
              name="efficiency_id"
              value={formData.efficiency_id}
              onChange={handleSelectGeneric}
              disabled={!efficiencyUnlocked || efficiency?.length === 0}
              required
            >
              <option value="" disabled>
                {t("standardProjectModal.selectOption")}
              </option>
              {efficiency?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>

          {/* Fluid type */}
          <FloatingLabel
            controlId="floating_fluid_type"
            label={t(`standardProjectModal.fluidType`)}
            className="mb-3"
          >
            <Form.Select
              name="fluid_type"
              value={formData.fluid_type}
              onChange={handleSelectGeneric}
            >
              <option value="" disabled>
                {t("standardProjectModal.selectOption")}
              </option>
              <option value="Water">{t(`standardProjectModal.water`)}</option>
              <option value="Propylene glycol">{t(`standardProjectModal.propyleneGlycol`)}</option>
            </Form.Select>
          </FloatingLabel>

          {/* Fluid Concentration */}
          {formData.fluid_type !== "Water" && (
            <FloatingLabel
              controlId="floating_fluid_concentration"
              label={t(`standardProjectModal.fluidConcentration`)}
              className="mb-3"
            >
              <Form.Select
                name="fluid_concentration"
                value={formData.fluid_concentration}
                onChange={handleSelectGeneric}
              >
                <option value="" disabled>
                  {t("standardProjectModal.selectOption")}
                </option>
                <option value={20}>20%</option>
                <option value={30}>30%</option>
                <option value={40}>40%</option>
                <option value={50}>50%</option>
                <option value={60}>60%</option>
              </Form.Select>
            </FloatingLabel>
          )}

          {/* Leaving Water Temperature / Evaporating Temperature */}
          <InputGroup className="mb-3">
            <FloatingLabel
              controlId="floatingLeavingTemp"
              label={
                isCondensingUnit
                  ? t("standardProjectModal.evaporatingTemperature")
                  : t("standardProjectModal.leavingWaterTemp")
              }
            >
              <Form.Control
                type="number"
                name="leaving_water_temperature"
                value={formData.leaving_water_temperature}
                onChange={handleChange}
                step="any"
                required
                placeholder=""
                onWheel={disableNumberScroll}
              />
            </FloatingLabel>
            <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
          </InputGroup>

          {/* Entering Water Temperature */}
          <InputGroup className="mb-3">
            <FloatingLabel
              controlId="floatingEnteringTemp"
              label={t("standardProjectModal.enteringWaterTemp")}
            >
              <Form.Control
                type="number"
                name="entering_water_temperature"
                value={formData.entering_water_temperature}
                onChange={handleChange}
                step="any"
                required
                placeholder=""
                onWheel={disableNumberScroll}
              />
            </FloatingLabel>
            <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
          </InputGroup>

          {/* Location */}
          <FloatingLabel
            controlId="floatingLocation"
            label={t("standardProjectModal.location")}
            className={`mb-3 ${formData.location ? "label-floating-fixed" : ""}`}
          >
            <StyledCreatableSelect
              isClearable
              options={locationOptions}
              classNamePrefix="react-select"
              value={
                locationOptions.find((opt) => opt.value === formData.location) ||
                (formData.location ? { label: formData.location, value: formData.location } : null)
              }
              onChange={(newValue) => {
                handleSelectGeneric({
                  target: {
                    name: "location",
                    value: newValue ? newValue.value : "",
                  },
                });
              }}
              formatCreateLabel={(inputValue) => `Add: "${inputValue}"`}
              placeholder={null}
            />
          </FloatingLabel>

          {/* Ambiance Temperature */}
          <InputGroup className="mb-3">
            <FloatingLabel
              controlId="floatingAmbiance"
              label={t("standardProjectModal.ambianceTemperature")}
            >
              <Form.Control
                type="number"
                name="ambiance_temperature"
                value={formData.ambiance_temperature}
                onChange={handleChange}
                step="any"
                required
                placeholder=""
                onWheel={disableNumberScroll}
              />
            </FloatingLabel>
            <InputGroup.Text>{t(`units.${unitSystem}.temperature`)}</InputGroup.Text>
          </InputGroup>

          {/* AMSL */}
          <InputGroup className="mb-3">
            <FloatingLabel controlId="floatingAMSL" label={t("standardProjectModal.amsl")}>
              <Form.Control
                type="number"
                name="amsl"
                value={formData.amsl}
                onChange={handleChange}
                required
                placeholder=""
                onWheel={disableNumberScroll}
              />
            </FloatingLabel>
            <InputGroup.Text>{t(`units.${unitSystem}.length`)}</InputGroup.Text>
          </InputGroup>

          {/* Running Capacity: solo el centrifugo la usa, es lo que se le pide al selector de
              Copeland. */}
          {formData.technology_id === "4" && (
            <InputGroup className="mb-3">
              <FloatingLabel
                controlId="floatingRunningCapacity"
                label={t("standardProjectModal.runningCapacity")}
              >
                <Form.Control
                  type="number"
                  name="running_capacity"
                  value={formData.running_capacity}
                  onChange={handleChange}
                  step="any"
                  placeholder=""
                  onWheel={disableNumberScroll}
                />
              </FloatingLabel>
              <InputGroup.Text>{t(`units.${unitSystem}.weight`)}</InputGroup.Text>
            </InputGroup>
          )}

          {/* Pressure Drop */}
          <InputGroup className="mb-3">
            <FloatingLabel
              controlId="floatingPressureDrop"
              label={t("standardProjectModal.pressureDrop")}
            >
              <Form.Control
                type="number"
                name="pressure_drop"
                value={formData.pressure_drop}
                onChange={handleChange}
                step="any"
                required
                placeholder=""
                onWheel={disableNumberScroll}
              />
            </FloatingLabel>
            <InputGroup.Text>
              ft.H<sub>2</sub>O
            </InputGroup.Text>
          </InputGroup>

          <ActionButton type="submit">{buttonLabel}</ActionButton>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default StandardProjectModal;
